import React, { useEffect, useState } from "react";
import BreadcrumbItem from "../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IIncident } from "../../shared/interfaces/incident/IIncident";
import { IncidentService } from "./service";
import { handleError } from "../../shared/functions/handleError";
import { usePagination } from "../../shared/hooks/usePagination";
import IncidentList from "./components/List";
import { Button, Card, CardHeader, Col, Form, Row } from "react-bootstrap";
import { IPagination } from "../../shared/interfaces/shared/IPagination";
import Select2 from "../../shared/components/Select2";
import AsyncSelect2 from "../../shared/components/AsyncSelect2";
import { InstitutionService } from "../institutions/service";

const Incidents = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [incidents, setIncidents] = React.useState<IIncident[]>([]);
    const [incidentTypes, setIncidentTypes] = useState<string[]>([]);
    const [statusTypes, setStatusTypes] = useState<string[]>([]);

    const {
        pagination,
        setPagination,
        changeFilterHandler,
        handleUpdateList,
        totalPages,
        setTotalPages,
    } = usePagination({
        perPage: 10,
    });

    const [localPagination, setLocalPagination] = useState<IPagination | null>(
        null,
    );

    const incidentTypeOptions = incidentTypes.map((type) => ({
        value: type,
        label: t(`incident.type.${type}`),
    }));

    const statusTypeOptions = statusTypes.map((status) => ({
        value: status,
        label: t(`incident.statusType.${status}`),
    }));

    const loadInstitutionOptions = (inputValue: string) => {
        return InstitutionService.getAll({
            page: 0,
            size: 50,
            name: inputValue,
        }).then((response) => {
            return response?.data?.map((institution) => ({
                value: institution.id,
                label: institution.name,
            }));
        });
    };

    const changeLocalPaginationHandler = (
        name: string,
        value: string | number | boolean | number[] | null,
    ) => {
        setLocalPagination((prev) => ({ ...prev, [name]: value }) as IPagination);
    };

    useEffect(() => {
        IncidentService.getAll(pagination)
            .then((response) => {
                setIncidents(response?.data);
                setTotalPages(response?.totalPages);
            })
            .catch(handleError);
    }, [pagination, setTotalPages]);

    useEffect(() => {
        IncidentService.getTypes()
            .then((response) => {
                setIncidentTypes(response?.data || []);
            })
            .catch(handleError);
        
        IncidentService.getStatusTypes()
            .then((response) => {
                setStatusTypes(response?.data || []);
            })
            .catch(handleError);
    }, []);

    return (
        <React.Fragment>
            <BreadcrumbItem
                mainTitle={t("incident.incidents")}
                subTitle={t("incident.incidents")}
                buttonTitle={t("incident.addIncident")}
                onCtaClick={() => navigate("/incidents/create")}
            />
            <Card>
                <Card.Header>
                    <h5>{t("incident.filterIncidents")}</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <AsyncSelect2
                                name="institutionId"
                                placeholder={t("institution.selectInstitution")}
                                loadOptions={loadInstitutionOptions}
                                onChange={(value) => {
                                    setLocalPagination(
                                        (prev) =>
                                            ({ ...prev, institutionId: value ?? undefined }) as IPagination,
                                    );
                                }}
                                value={localPagination?.institutionId as number}
                                isClearable
                            />
                        </Col>
                        <Col md={3}>
                            <Select2<string>
                                name="incidentType"
                                placeholder={t("incident.selectType")}
                                options={incidentTypeOptions}
                                onChange={(value) => {
                                    setLocalPagination(
                                        (prev) =>
                                            ({ ...prev, incidentType: value ?? undefined }) as IPagination,
                                    );
                                }}
                                value={localPagination?.incidentType as string}
                                isClearable
                            />
                        </Col>
                        <Col md={3}>
                            <Select2<string>
                                name="incidentStatusType"
                                placeholder={t("incident.selectStatus")}
                                options={statusTypeOptions}
                                onChange={(value) => {
                                    setLocalPagination(
                                        (prev) =>
                                            ({ ...prev, incidentStatusType: value ?? undefined }) as IPagination,
                                    );
                                }}
                                value={localPagination?.incidentStatusType as string}
                                isClearable
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Check
                                type="switch"
                                id="drillMode-filter"
                                label={t("incident.drillModeOnly")}
                                checked={localPagination?.drillMode === true}
                                onChange={(e) =>
                                    changeLocalPaginationHandler(
                                        "drillMode",
                                        e.target.checked ? true : null
                                    )
                                }
                                className="mt-2"
                            />
                        </Col>
                    </Row>
                </Card.Body>
                <Card.Footer className={"text-end"}>
                    <Button
                        variant={"light"}
                        onClick={() => {
                            setLocalPagination(null);
                            setPagination({ page: 0, size: 20 });
                        }}
                    >
                        {t("button.reset")}
                    </Button>
                    <Button
                        variant={"primary"}
                        className={"ms-3"}
                        onClick={() => {
                            setPagination((prev: IPagination) => ({
                                ...prev,
                                ...localPagination,
                            }));
                        }}
                    >
                        {t("button.search")}
                    </Button>
                </Card.Footer>
            </Card>
            <Card className="table-card">
                <CardHeader>
                    <h5>{t("incident.incidentList")}</h5>
                </CardHeader>

                <Card.Body className="pt-3">
                    <IncidentList
                        incidents={incidents}
                        pagination={pagination}
                        totalPages={totalPages}
                        handleUpdateList={handleUpdateList}
                        changeFilterHandler={changeFilterHandler}
                    />
                </Card.Body>
            </Card>
        </React.Fragment>
    );
};

export default Incidents;
